import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMemoPad } from '@fortawesome/pro-light-svg-icons/faMemoPad';

import React, { useMemo, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useModal } from '../../../../hooks/use-modal';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { AccessibleEmoji } from 'ui-components/components/accessible-emoji';

import { useOnboardingLabelStepComplete, useLabelOutput } from '../../../../hooks/use-seller-setting';
import OnboardingStep from '../../steps';
import { SelectLabelLayoutModal } from '../../../../components/modals/select-label-layout-modal';
import messages from './messages';
import style from '../style.module.less';
import ConnectedList from '../connected-list';
import { createLookupHash } from '../../../../services/hash-service';
import { EventSourceStrings, Workflows } from '../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../tracking/use-workflow-tracking';
import { getAllLayoutOptionsMapped } from '../../../../components/layout-selector/utils';
import { useEnableNewLayoutOptions } from '../../../../feature-flags/hooks/use-enable-new-layout-options';

const LabelStep = () => {
  const intl = useIntl();
  const isStepComplete = useOnboardingLabelStepComplete();
  const sellerLabelFormat = useLabelOutput();
  const { open, close, isOpen } = useModal();
  const trackLabelLayoutSelection = useTrackWorkflow(Workflows.LABEL_LAYOUT_SELECTION_FLOW);
  const enableNewLayoutOptions = useEnableNewLayoutOptions();

  const connectedLabelFormatData = useMemo(() => {
    const labelOptionsHashMap = createLookupHash(getAllLayoutOptionsMapped(enableNewLayoutOptions), 'value');

    const { messageDescriptor } = labelOptionsHashMap[sellerLabelFormat];
    return [
      {
        name: intl.formatMessage(messageDescriptor),
        id: sellerLabelFormat,
        logo: <FontAwesomeIcon icon={faMemoPad} />,
      },
    ];
  }, [sellerLabelFormat, intl, enableNewLayoutOptions]);

  const onSuccess = useCallback(() => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      title: <FormattedMessage {...messages.saveSuccessMessage} />,
      content: '',
      duration: 10000,
    });
  }, []);

  const onClick = useCallback(() => {
    open();
    trackLabelLayoutSelection(WorkflowSteps.START, {
      source: EventSourceStrings.ONBOARD_PAGE,
    });
  }, [open, trackLabelLayoutSelection]);

  return (
    <>
      <OnboardingStep
        addAnotherMessage={<FormattedMessage {...messages.changeMessage} />}
        buttonTitle={<FormattedMessage {...messages.addMessage} />}
        connectedList={<ConnectedList items={connectedLabelFormatData} />}
        description={<FormattedMessage {...messages.description} />}
        isComplete={isStepComplete}
        onClick={onClick}
        stepTitle={
          <FormattedMessage
            {...messages.title}
            values={{
              emoji: <AccessibleEmoji emoji='🖨' emojiName='printer' />,
              span: (chunks) => <span className={style.emoji}>{chunks}</span>,
            }}
          />
        }
      />
      {isOpen && <SelectLabelLayoutModal isStepComplete={isStepComplete} onClose={close} onSuccess={onSuccess} />}
    </>
  );
};

export default LabelStep;
